/**
 * Authentication Service Module
 *
 * This module provides core authentication functionality for the Auriga application,
 * handling user authentication flows, session management, and authentication state.
 *
 * It uses AWS Amplify Auth v6+ for authentication operations and implements
 * federated authentication with Cognito Identity Pools.
 */

import { AuthUser, FetchUserAttributesOutput } from '@aws-amplify/auth';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signInWithRedirect, signOut } from 'aws-amplify/auth';

import { getCognitoSettings } from './settings';

// The LocalStorage key used to store the redirect href used after successful authentication.
const LOCAL_STORAGE_REDIRECT_KEY = 'redirectAfterAuth';

// Track initialization state to prevent operations before configuration
let isInitialized = false;

/**
 * Sets the initialized status of the auth module
 *
 * This function is called after Amplify is successfully configured
 * to indicate that authentication operations can proceed.
 *
 * @param newInitStatus - Boolean indicating if auth is initialized
 */
export const setInitialized = (newInitStatus: boolean) => {
  isInitialized = newInitStatus;
};

/**
 * Verifies that the auth module is properly initialized
 *
 * This function is called before authentication operations to ensure
 * that Amplify has been configured with the necessary settings.
 *
 * @throws Error if the auth module is not initialized
 */
export const checkInitialized = () => {
  if (!isInitialized) {
    throw new Error('Auth is not initialized. Ensure Amplify is configured before performing auth operations.');
  }
};

/**
 * Initiates federated authentication flow
 *
 * - Verifies that auth is initialized
 * - Stores the current URL for post-authentication redirect
 * - Retrieves Cognito settings with the identity provider name
 * - Initiates the federated sign-in flow with Amplify v6+
 *
 * Note: This function will redirect the browser to the identity provider's
 * login page, so any code after calling this function may not execute immediately.
 *
 * @returns A promise that resolves when the sign-in process is complete
 * @throws Error if authentication fails or auth is not initialized
 */
export const authenticateUser = async (): Promise<void> => {
  checkInitialized();
  try {
    // Store the current URL to redirect back after authentication is complete
    localStorage.setItem(LOCAL_STORAGE_REDIRECT_KEY, window.location.href);

    // Get the Cognito settings for grabbing the Identity Provider
    const cognitoSettings = getCognitoSettings();

    // Applies to Federated sign-in in Amplify v6+
    await signInWithRedirect({
      provider: {
        custom: cognitoSettings.identityProvider,
      },
    });
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

/**
 * Handles redirect after authentication
 *
 * Checks for a stored redirect URL in localStorage and redirects the user
 * to that URL, then removes the stored URL.
 *
 * This is typically called on application startup to redirect users
 * back to the original location after successful authentication.
 */
export const handleRedirectAfterAuth = () => {
  const redirectAfterAuth = localStorage.getItem(LOCAL_STORAGE_REDIRECT_KEY);
  if (redirectAfterAuth) {
    // User has recently authenticated, redirect them back to where their original url.
    localStorage.removeItem(LOCAL_STORAGE_REDIRECT_KEY);

    // Only redirect if the current location does NOT match the redirect destination.
    if (window.location.href !== redirectAfterAuth) {
      window.location.replace(redirectAfterAuth);
    }
  }
};

/**
 * Signs out the current user
 *
 * - Calls Amplify's signOut method to clear authentication tokens
 * - Handles any errors that occur during sign-out
 *
 * @returns A promise that resolves when the sign-out process is complete
 * @throws Error if sign-out fails
 */
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut();
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Retrieves the currently authenticated user and any known attributes
 *
 * Steps:
 * - Attempts to refresh the authentication session
 * - Retrieves the current user information
 * - Retrieves user attributes with fetchUserAttributes
 * - Returns null if no authenticated user is found
 *
 * @returns A promise that resolves to the current authenticated user or null, and any user attributes available
 */
export const getAuthenticatedUserWithAttributes = async (): Promise<{
  attributes: FetchUserAttributesOutput;
  user: AuthUser;
} | null> => {
  try {
    // Try to get the current session, and refresh tokens if needed
    await fetchAuthSession();
    const user = await getCurrentUser();

    // Fetch any User attributes
    const attributes = await fetchUserAttributes();

    // Returned combined object with results
    return { user, attributes };
  } catch (error) {
    console.debug('Error getting authenticated user:', error);
    return null;
  }
};

/**
 * Verifies if the user is currently authenticated
 *
 * - Attempts to refresh the authentication session
 * - Verifies that a current user exists
 * - Returns a boolean indicating authentication status
 *
 * @returns A promise that resolves to true if authenticated, false otherwise
 */
export const checkAuthentication = async () => {
  try {
    // Try to get the current session to refresh tokens if needed
    await fetchAuthSession();
    await getCurrentUser();
    return true;
  } catch (error) {
    console.error('Error checking authentication status:', error);
    return false;
  }
};
